import React, { createContext, useContext, useState, useEffect } from 'react'
import { ThemeProvider } from '@emotion/react'
import { lightTheme, darkTheme } from '@utils/theme'
import { flushSync } from 'react-dom'

type ThemeMode = 'light' | 'dark'

interface ThemeContextProps {
  mode: ThemeMode
  toggleTheme: (e?: React.MouseEvent) => void
}

const ThemeContext = createContext<ThemeContextProps>({
  mode: 'dark',
  toggleTheme: () => {},
})

export const useThemeToggle = () => useContext(ThemeContext)

export const CustomThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [mode, setMode] = useState<ThemeMode>('dark')

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme-mode') as ThemeMode
    if (savedTheme) {
      setMode(savedTheme)
    } else {
      localStorage.setItem('theme-mode', 'dark')
    }
  }, [])

  const toggleTheme = (e?: React.MouseEvent) => {
    const isDark = document.body.classList.contains('dark')

    const applyToggle = () => {
      setMode((prev) => {
        const newMode = prev === 'light' ? 'dark' : 'light'
        localStorage.setItem('theme-mode', newMode)
        return newMode
      })
    }

    if (!(document as any).startViewTransition || !e) {
      applyToggle()
      return
    }

    // If currently dark (switching to light), expand from bottom-left corner
    // Otherwise (switching to dark), expand from the button click (top-right)
    const x = isDark ? 0 : e.clientX
    const y = isDark ? window.innerHeight : e.clientY

    const endRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y),
    )

    const transition = (document as any).startViewTransition(() => {
      flushSync(() => {
        applyToggle()
      })
    })

    transition.ready.then(() => {
      const clipPath = [
        `circle(0px at ${x}px ${y}px)`,
        `circle(${endRadius}px at ${x}px ${y}px)`,
      ]

      document.documentElement.animate(
        {
          clipPath: clipPath,
        },
        {
          duration: 500,
          easing: 'ease-in-out',
          pseudoElement: '::view-transition-new(root)',
        },
      )
    })
  }

  const currentTheme = mode === 'light' ? lightTheme : darkTheme

  // Important: also set a class on body or html so global css can use it if needed
  useEffect(() => {
    if (mode === 'dark') {
      document.body.classList.add('dark')
    } else {
      document.body.classList.remove('dark')
    }
  }, [mode])

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <ThemeProvider theme={currentTheme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  )
}
